// Generated by scripts/sync-measurement-sdk.cjs from Bullstory Next.
// 유입 소스 분류 + first-touch attribution.
// PageShell.tsx의 페이지뷰 ping과 결제 confirm flow가 둘 다 사용.
// 첫 진입 시 분류 후 localStorage에 영구 저장 → 가입 시점에 그 source를 attribution.
// 분류 우선순위: UTM → 확인된 검색 referrer → UA 인앱 토큰 → 그 밖의 referrer → 'direct'

import { NEWSLETTER_CAMPAIGNS } from './newsletter-locales';

const STORAGE_KEY = 'bs_src';
const SESSION_ID_KEY = 'bs_measurement_sid';
const SESSION_ACTIVITY_KEY = 'bs_measurement_sid_at';
const SESSION_ATTRIBUTION_KEY = 'bs_measurement_session_attribution';
export const MEASUREMENT_SESSION_TIMEOUT_MS = 30 * 60 * 1000;
export const MEASUREMENT_SESSION_TOUCH_THROTTLE_MS = 60 * 1000;
export const AGENT_ORIGIN_MEASUREMENT_SCHEMA = 'agent-origin-v1';
const MEASUREMENT_ACTIVITY_EVENTS = ['pointerdown', 'click', 'keydown'] as const;

export interface MeasurementSessionAttribution {
  sessionId: string;
  sessionSource: string;
  sessionEntryPath: string;
}

export interface PageMeasurementIdentity {
  path: string;
  pageviewId: string;
  pageviewOccurredAt: string;
  sessionId: string;
  sessionSource: string;
  sessionEntryPath: string;
}

export interface PageMeasurementAttribution extends MeasurementSessionAttribution {
  pagePath: string;
  pageviewId: string;
  pageviewOccurredAt: string;
  source: string;
  entryPath: string;
  firstSource: string;
  firstEntryPath: string;
}

interface PageMeasurementIdentityOptions {
  createPageview?: () => string;
  createPageviewOccurredAt?: () => string;
  getSession?: (
    entryPath: string,
    isDocumentEntry: boolean,
  ) => MeasurementSessionAttribution;
  isDocumentEntry?: boolean;
}

let activePageMeasurementIdentity: PageMeasurementIdentity | null = null;

export interface CrossProductOrigin {
  eventId: string;
  pagePath: string;
  pageviewId: string;
  sessionId: string;
  createdAt: number;
}

export function createBiteHandoff(href: string): { href: string; eventId: string } | null {
  const page = getActivePageMeasurementIdentity(window.location.pathname);
  const visitorId = getSharedVisitorId();
  if (!page || !visitorId) return null;
  const target = new URL(href);
  if (target.origin !== 'https://bite.cx' || (target.pathname !== '/' && !target.pathname.startsWith('/bite/'))) return null;
  const eventId = createPageviewId();
  const attribution = createPageMeasurementAttribution(page, getStoredSource(), getStoredEntryPath());
  const payload = { version: 1, visitorId, ...attribution, eventId, createdAt: Date.now() };
  target.hash = new URLSearchParams({ bs_handoff: JSON.stringify(payload) }).toString();
  return { href: target.href, eventId };
}

/** Consuming applications must call this only after analytics consent. Never transfers auth. */
export function importBiteHandoff(): CrossProductOrigin | null {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const raw = hash.get('bs_handoff');
  if (!raw) return null;
  hash.delete('bs_handoff');
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + (hash.size ? `#${hash}` : ''));
  try {
    const p = JSON.parse(raw);
    if (raw.length > 5000 || p.version !== 1 || !isValidMeasurementVisitorId(p.visitorId)
      || !isValidMeasurementId(p.eventId) || !isValidMeasurementId(p.pageviewId)
      || !isValidMeasurementId(p.sessionId) || !normalizeMeasurementPagePath(p.pagePath)
      || !Number.isFinite(p.createdAt) || Date.now() - p.createdAt > MEASUREMENT_SESSION_TIMEOUT_MS
      || p.createdAt > Date.now() + 60000) return null;
    const firstSource = typeof p.firstSource === 'string' && /^[a-z0-9:_-]{1,32}$/i.test(p.firstSource) ? p.firstSource : '';
    const firstEntry = normalizeMeasurementPagePath(p.firstEntryPath);
    const sessionSource = typeof p.sessionSource === 'string' && /^[a-z0-9:_-]{1,32}$/i.test(p.sessionSource) ? p.sessionSource : '';
    const sessionEntry = normalizeMeasurementPagePath(p.sessionEntryPath);
    if (!firstSource || !firstEntry || !sessionSource || !sessionEntry) return null;
    sharedSet('bs_vid', p.visitorId);
    sharedSet(STORAGE_KEY, firstSource);
    sharedSet('bs_entry', firstEntry);
    sharedSet(SESSION_ID_KEY, p.sessionId);
    sharedSet(SESSION_ACTIVITY_KEY, String(Date.now()));
    sharedSet(SESSION_ATTRIBUTION_KEY, JSON.stringify({ sessionId: p.sessionId, sessionSource, sessionEntryPath: sessionEntry }));
    return { eventId: p.eventId, pagePath: p.pagePath, pageviewId: p.pageviewId, sessionId: p.sessionId, createdAt: p.createdAt };
  } catch { return null; }
}

// ── 서브도메인 공유 저장 (.bullstory.io 쿠키 우선, localStorage는 폴백/마이그레이션) ──
// localStorage는 origin 단위라 bullstory.io에서 잡은 first-touch가 premium.bullstory.io 가입 시점에
// 보이지 않음 → 출처가 direct로 세탁됨. 1년짜리 1st-party 쿠키로 도메인 전체에서 공유.
function readCookie(name: string): string {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch { return ''; }
}
function writeCookie(name: string, value: string): void {
  try {
    const base = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
    if (window.location.hostname.endsWith('bullstory.io')) {
      document.cookie = `${base}; domain=.bullstory.io`;
    } else {
      document.cookie = base; // 로컬/프리뷰 — host-only
    }
  } catch { /* no-op */ }
}
function sharedGet(key: string): string {
  const fromCookie = readCookie(key);
  if (fromCookie) return fromCookie;
  try {
    const fromLs = localStorage.getItem(key);
    if (fromLs) { writeCookie(key, fromLs); return fromLs; } // 기존 값 쿠키로 승격
  } catch { /* no-op */ }
  return '';
}
function sharedSet(key: string, value: string): void {
  writeCookie(key, value);
  try { localStorage.setItem(key, value); } catch { /* no-op */ }
}

/** Called by consent-gated consumers when analytics consent is withdrawn. */
export function clearMeasurementIdentity(): void {
  for (const key of ['bs_vid', 'bullstory_visitor_id', STORAGE_KEY, 'bs_entry', SESSION_ID_KEY, SESSION_ACTIVITY_KEY, SESSION_ATTRIBUTION_KEY]) {
    try { localStorage.removeItem(key); } catch { /* storage denied */ }
    const expired = `${key}=; path=/; max-age=0; SameSite=Lax`;
    document.cookie = expired;
    if (window.location.hostname.endsWith('bullstory.io')) document.cookie = `${expired}; domain=.bullstory.io`;
  }
  setActivePageMeasurementIdentity(null);
}

function randomMeasurementId(prefix: string): string {
  const random = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `${prefix}_${random}`.slice(0, 64);
}

export function isValidMeasurementId(value: string): boolean {
  return /^[a-z0-9_-]{8,64}$/i.test(value);
}

export function isValidMeasurementInstant(value: string): boolean {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

function validMeasurementSource(value: string): boolean {
  return /^[a-z0-9:_-]{1,32}$/i.test(value)
    && !containsMeasurementDirectIdentifier(value);
}

const MEASUREMENT_PRIVATE_ENTRY_PREFIXES = [
  '/admin',
  '/api',
  '/auth',
  '/login',
  '/logout',
  '/checkout',
  '/redeem',
  '/reset',
  '/download',
] as const;

const MEASUREMENT_DIRECT_IDENTIFIER_PATTERNS = [
  /@/,
  /(?:^|\/)\d{2,3}-?\d{3,4}-?\d{4}(?:\/|$)/,
  /(?:^|\/)\d{6}-?[1-4]\d{6}(?:\/|$)/,
  /(?:^|\/)[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?:\/|$)/i,
  /(?:^|\/)(?:cus|sub|cs|in|pi|pm)_[0-9a-z]{6,}(?:\/|$)/i,
  /(?:^|\/)eyj[0-9a-z_-]{5,}\.[0-9a-z_-]{5,}\.[0-9a-z_-]{5,}(?:\/|$)/i,
  /(?:^|\/)bcl_[0-9a-z_-]{8,}(?:\/|$)/i,
  /\bbearer\s+[0-9a-z._~-]{8,}/i,
  /\b(?:sk|rk|pk)_(?:live|test)_[0-9a-z_-]{8,}\b/i,
  /\bwhsec_[0-9a-z_-]{8,}\b/i,
  /(?:^|[/:;&,\s])(?:access_?token|refresh_?token|token|code|state|secret|api_?key|session(?:_id)?)=[^/?#;&,\s]+/i,
] as const;

const MEASUREMENT_SECRET_PATH_PATTERNS = [
  /(?:^|\/)agent\/redeem\/[^/?#]+(?:\/|$)/i,
  /(?:^|\/)redeem\/bcl_[^/?#]+(?:\/|$)/i,
] as const;

function measurementEntryPathShape(value: string): boolean {
  return value.startsWith('/')
    && !value.startsWith('//')
    && value.length <= 400
    && !/[\u0000-\u001f\u007f\\]/.test(value);
}

function decodeMeasurementEntryPath(value: string): string {
  let decoded = value;
  for (let depth = 0; depth < 8; depth += 1) {
    const next = decodeURIComponent(decoded);
    if (next === decoded) return decoded;
    decoded = next;
  }
  return decoded;
}

export function containsMeasurementDirectIdentifier(value: string): boolean {
  let decoded: string;
  try {
    decoded = decodeMeasurementEntryPath(value);
  } catch {
    return true;
  }
  return /%[0-9a-f]{2}/i.test(decoded)
    || MEASUREMENT_DIRECT_IDENTIFIER_PATTERNS.some((pattern) => pattern.test(decoded))
    || MEASUREMENT_SECRET_PATH_PATTERNS.some((pattern) => pattern.test(decoded));
}

export function isValidMeasurementVisitorId(value: string): boolean {
  if (!/^[0-9a-z._~-]{8,64}$/i.test(value)) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
    return true;
  }
  return !containsMeasurementDirectIdentifier(`/${value}`);
}

export function normalizeMeasurementPagePath(value: string): string {
  const path = String(value || '').trim();
  if (!measurementEntryPathShape(path)) return '';
  let decoded: string;
  try {
    decoded = decodeMeasurementEntryPath(path);
  } catch {
    return '';
  }
  if (/%[0-9a-f]{2}/i.test(decoded) || !measurementEntryPathShape(decoded) || /[?#]/.test(decoded)) return '';
  let pathname: string;
  try {
    pathname = new URL(decoded, 'https://bullstory.io').pathname;
  } catch {
    return '';
  }
  const comparablePath = pathname.toLowerCase();
  if (MEASUREMENT_PRIVATE_ENTRY_PREFIXES.some((prefix) => (
    comparablePath === prefix || comparablePath.startsWith(`${prefix}/`)
  ))) return '';
  return containsMeasurementDirectIdentifier(pathname) ? '' : pathname;
}

/** 원시 검색어와 fragment 및 사용자 정보를 버리고 referrer 출처만 보존합니다. */
export function normalizeMeasurementReferrer(value: string): string {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.origin : '';
  } catch {
    return '';
  }
}

/** 공유 visitorId — UV dedup 키. 쿠키(bs_vid) 우선, 구 localStorage 키들에서 마이그레이션. */
export function getSharedVisitorId(): string {
  if (typeof window === 'undefined') return '';
  let id = sharedGet('bs_vid');
  if (!isValidMeasurementVisitorId(id)) {
    try { id = localStorage.getItem('bullstory_visitor_id') || ''; } catch { id = ''; }
  }
  if (!isValidMeasurementVisitorId(id)) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
  sharedSet('bs_vid', id);
  return sharedGet('bs_vid') === id && isValidMeasurementVisitorId(id) ? id : '';
}

function classifyUAInapp(ua: string): string | null {
  if (!ua) return null;
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB|FB4A|FBIOS/i.test(ua)) return 'facebook';
  if (/KAKAOTALK/i.test(ua)) return 'kakao';
  if (/Twitter\/|TwitterAndroid/i.test(ua)) return 'twitter';
  if (/Threads/i.test(ua)) return 'threads';
  if (/TelegramMessenger/i.test(ua)) return 'telegram';
  if (/NAVER\(|NaverSearch|NAVER /i.test(ua)) return 'naver-app';
  if (/\bGSA\//i.test(ua)) return 'google-app';
  if (/DaumApps|Daum\//i.test(ua)) return 'daum-app';
  if (/Line\//i.test(ua)) return 'line';
  if (/Pinterest/i.test(ua)) return 'pinterest';
  if (/KakaoStory/i.test(ua)) return 'kakao';
  if (/BAND\//i.test(ua)) return 'band';
  if (/Snapchat/i.test(ua)) return 'snapchat';
  return null;
}

const SEARCH_SOURCE_NAMES = new Set([
  'baidu',
  'bing',
  'daum',
  'duckduckgo',
  'google',
  'naver',
  'yahoo',
]);

const EXPLICIT_ORGANIC_MEDIA = new Set(['organic', 'search']);
const NEWSLETTER_CONTENT = new Set(['article', 'company', 'market', 'bst']);

function normalizeCampaignValue(value: string): string {
  const normalized = value.toLowerCase();
  return /^[a-z0-9_-]{1,32}$/.test(normalized) ? normalized : '';
}

/** 검색형 UTM만 허용 목록으로 줄여 원시 검색어와 임의 캠페인 식별값을 원장에 보내지 않습니다. */
export function getSafeSearchCampaign(): Record<'utm_source' | 'utm_medium', string> | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    const source = normalizeCampaignValue(params.get('utm_source') || '');
    const medium = normalizeCampaignValue(params.get('utm_medium') || '');
    if (
      (source === 'search' || SEARCH_SOURCE_NAMES.has(source))
      && EXPLICIT_ORGANIC_MEDIA.has(medium)
    ) {
      return { utm_source: source, utm_medium: medium };
    }
  } catch {
    return undefined;
  }
  return undefined;
}

/** 원시 사용자 값을 버리고 검색 또는 뉴스레터의 고정 캠페인 값만 보존합니다. */
export function getSafeMeasurementCampaign(): Record<string, string> | undefined {
  const search = getSafeSearchCampaign();
  if (search) return search;
  if (typeof window === 'undefined') return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    const source = normalizeCampaignValue(params.get('utm_source') || '');
    const medium = normalizeCampaignValue(params.get('utm_medium') || '');
    const campaign = normalizeCampaignValue(params.get('utm_campaign') || '');
    const content = normalizeCampaignValue(params.get('utm_content') || '');
    if (
      source === 'newsletter'
      && medium === 'email'
      && NEWSLETTER_CAMPAIGNS.has(campaign)
      && NEWSLETTER_CONTENT.has(content)
    ) {
      return {
        utm_source: source,
        utm_medium: medium,
        utm_campaign: campaign,
        utm_content: content,
      };
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function normalizedCampaignSource(value: string, medium: string): string {
  const clean = normalizeCampaignValue(value);
  const cleanMedium = normalizeCampaignValue(medium);
  if (clean === 'search') {
    return EXPLICIT_ORGANIC_MEDIA.has(cleanMedium) ? clean : 'campaign:search';
  }
  if (SEARCH_SOURCE_NAMES.has(clean) && EXPLICIT_ORGANIC_MEDIA.has(cleanMedium)) {
    return `search:${clean}`;
  }
  if (SEARCH_SOURCE_NAMES.has(clean)) return `campaign:${clean}`;
  return clean;
}

function isGoogleSearchHost(host: string): boolean {
  return /^(?:www\.)?google\.(?:com|[a-z]{2,3}|com\.[a-z]{2}|co\.[a-z]{2})$/.test(host);
}

function googleSearchReferrer(referrerUrl: URL): boolean {
  if (!isGoogleSearchHost(referrerUrl.hostname.toLowerCase())) return false;
  const path = referrerUrl.pathname.toLowerCase().replace(/\/+$/, '') || '/';
  // 실제 결과는 /search이고 cross-origin referrer 정책은 이를 origin의 /로 줄일 수 있습니다.
  return path === '/' || path === '/search';
}

function searchSourceFromHost(host: string): string | null {
  if (host === 'search.naver.com' || host === 'm.search.naver.com') return 'search:naver';
  if (host === 'www.bing.com' || host === 'cn.bing.com') return 'search:bing';
  if (host === 'search.daum.net') return 'search:daum';
  if (host === 'duckduckgo.com' || host === 'html.duckduckgo.com') return 'search:duckduckgo';
  if (host === 'search.yahoo.com' || host === 'search.yahoo.co.jp') return 'search:yahoo';
  if (host === 'www.baidu.com' || host === 'm.baidu.com') return 'search:baidu';
  return null;
}

function referrerHost(referrer: string): string {
  if (!referrer) return '';
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function searchSourceFromReferrer(referrer: string): string | null {
  let referrerUrl: URL;
  try {
    referrerUrl = new URL(referrer);
  } catch {
    return null;
  }
  if (googleSearchReferrer(referrerUrl)) return 'search:google';
  return searchSourceFromHost(referrerUrl.hostname.toLowerCase());
}

function normalizedReferrerSource(host: string): string {
  if (!host) return 'other';
  if (host === 'bite.cx' || /\.bite\.cx$/.test(host)) return 'bitecx';
  if (/instagram\./.test(host)) return 'instagram';
  if (host === 't.me' || /telegram\./.test(host)) return 'telegram';
  if (/threads\./.test(host)) return 'threads';
  if (/twitter\.|x\.com|t\.co/.test(host)) return 'twitter';
  if (/facebook\.|fb\.com|fb\.me/.test(host)) return 'facebook';
  if (/kakao\.|daumcdn\./.test(host)) return 'kakao';
  if (/youtube\.|youtu\.be/.test(host)) return 'youtube';
  if (/bullstory\.|bullsresearch\.|bullstore\.|tosspayments\./.test(host)) return 'internal';
  if (/msn\.com/.test(host)) return 'direct';
  return 'other';
}

function classifyFromEnv(): string {
  // 1) UTM 우선
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = params.get('utm_source');
    if (utm) {
      if (normalizeCampaignValue(utm) === 'newsletter') {
        return getSafeMeasurementCampaign()?.utm_source === 'newsletter' ? 'newsletter' : 'other';
      }
      const clean = normalizedCampaignSource(utm, params.get('utm_medium') || '');
      if (clean) return clean;
    }
  } catch { /* no-op */ }

  const referrer = (typeof document !== 'undefined' && document.referrer) || '';

  // 2) 검색 결과 화면이 확인된 referrer만 검색엔진 출처로 올립니다.
  const searchSource = searchSourceFromReferrer(referrer);
  if (searchSource) return searchSource;

  // 3) 검색 앱 UA만으로 organic 유입을 추정하지 않습니다.
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const inapp = classifyUAInapp(ua);
  if (inapp) return inapp;

  // 4) 그 밖의 referrer host
  if (!referrer) return 'direct';
  return normalizedReferrerSource(referrerHost(referrer));
}

/** Reads the document entry without creating identifiers or writing storage. */
export function captureMeasurementEntry() {
  const source = classifyFromEnv();
  return {
    source: source === 'internal' ? 'direct' : source,
    path: normalizeMeasurementPagePath(window.location.pathname),
  };
}

/** first-touch source — 첫 호출 시 분류해서 localStorage에 영구 저장.
 *  internal(사이트 내부 referer)은 저장하지 않고 direct로 반환. */
export function getStoredSource(entrySource?: string): string {
  try {
    const stored = sharedGet(STORAGE_KEY);
    if (validMeasurementSource(stored)) return stored;
    const src = entrySource && validMeasurementSource(entrySource) ? entrySource : classifyFromEnv();
    if (src && src !== 'internal') {
      sharedSet(STORAGE_KEY, src);
      return src;
    }
    return src === 'internal' ? 'direct' : src;
  } catch { return ''; }
}

const ENTRY_PATH_KEY = 'bs_entry';

/** first-touch entry path — 첫 진입 페이지 path를 영구 저장.
 *  글별 전환율 계산용 (이 글에서 가입까지 이어진 사람 수). */
export function getStoredEntryPath(entryPath?: string): string {
  try {
    const stored = normalizeMeasurementPagePath(sharedGet(ENTRY_PATH_KEY));
    if (stored) return stored;
    const path = normalizeMeasurementPagePath(
      entryPath || (typeof window !== 'undefined' && window.location.pathname) || '/',
    );
    if (!path) return '';
    sharedSet(ENTRY_PATH_KEY, path);
    return path;
  } catch { return ''; }
}

function readSessionAttribution(sessionId: string): MeasurementSessionAttribution | null {
  if (!isValidMeasurementId(sessionId)) return null;
  try {
    const parsed = JSON.parse(sharedGet(SESSION_ATTRIBUTION_KEY));
    const sessionSource = String(parsed?.sessionSource || '');
    const sessionEntryPath = normalizeMeasurementPagePath(parsed?.sessionEntryPath);
    if (
      parsed?.sessionId !== sessionId
      || !validMeasurementSource(sessionSource)
      || !sessionEntryPath
    ) {
      return null;
    }
    return { sessionId, sessionSource, sessionEntryPath };
  } catch {
    return null;
  }
}

function currentSessionSource(isDocumentEntry: boolean): string {
  if (!isDocumentEntry) return 'direct';
  const source = classifyFromEnv();
  return source === 'internal' ? 'direct' : source;
}

/** 30분 비활성 기준 측정 세션 ID입니다. 저장소가 차단되면 빈 문자열을 반환합니다. */
export function getMeasurementSessionId(at = Date.now(), forceNew = false): string {
  if (typeof window === 'undefined' || !Number.isFinite(at)) return '';
  const storedId = sharedGet(SESSION_ID_KEY);
  const lastActivityAt = Number(sharedGet(SESSION_ACTIVITY_KEY));
  const inactiveMs = at - lastActivityAt;
  const reusable = !forceNew
    && isValidMeasurementId(storedId)
    && Number.isFinite(lastActivityAt)
    && inactiveMs >= 0
    && inactiveMs < MEASUREMENT_SESSION_TIMEOUT_MS;
  const sessionId = reusable ? storedId : randomMeasurementId('s');
  sharedSet(SESSION_ID_KEY, sessionId);
  sharedSet(SESSION_ACTIVITY_KEY, String(Math.round(at)));
  return sharedGet(SESSION_ID_KEY) === sessionId ? sessionId : '';
}

/** 세션 ID와 같은 수명으로 고정되는 현재 유입 출처와 진입 경로입니다. */
export function getMeasurementSessionAttribution(
  entryPath: string,
  isDocumentEntry: boolean,
  at = Date.now(),
  entrySource?: string,
): MeasurementSessionAttribution {
  let sessionId = getMeasurementSessionId(at);
  let stored = readSessionAttribution(sessionId);
  const classifiedSource = isDocumentEntry && entrySource && validMeasurementSource(entrySource)
    ? entrySource : currentSessionSource(isDocumentEntry);
  if (
    isDocumentEntry
    && classifiedSource === 'newsletter'
    && stored
    && stored.sessionSource !== classifiedSource
  ) {
    sessionId = getMeasurementSessionId(at, true);
    stored = null;
  }
  if (stored) return stored;

  const sessionEntryPath = normalizeMeasurementPagePath(entryPath);
  const sessionSource = validMeasurementSource(classifiedSource) ? classifiedSource : 'other';
  const attribution = { sessionId, sessionSource, sessionEntryPath };
  if (sessionId && sessionEntryPath) {
    sharedSet(SESSION_ATTRIBUTION_KEY, JSON.stringify(attribution));
  }
  return attribution;
}

/** 현재 페이지가 실제 사용자 활동을 관측했을 때 같은 측정 세션의 비활성 시각을 갱신합니다. */
export function touchMeasurementSession(sessionId: string, at = Date.now()): void {
  if (!isValidMeasurementId(sessionId) || !Number.isFinite(at)) return;
  if (sharedGet(SESSION_ID_KEY) !== sessionId) return;
  const lastActivityAt = Number(sharedGet(SESSION_ACTIVITY_KEY));
  const inactiveMs = at - lastActivityAt;
  if (!Number.isFinite(lastActivityAt) || inactiveMs < 0 || inactiveMs >= MEASUREMENT_SESSION_TIMEOUT_MS) return;
  sharedSet(SESSION_ACTIVITY_KEY, String(Math.round(at)));
}

/** 페이지뷰와 체류 이벤트가 함께 사용하는 비식별 페이지뷰 ID입니다. */
export function createPageviewId(): string {
  return randomMeasurementId('p');
}

/** 전송 재시도와 무관하게 논리 이벤트 생성 시점에 한 번 고정하는 UTC 시각입니다. */
export function createMeasurementOccurredAt(at = Date.now()): string {
  if (!Number.isFinite(at)) return '';
  return new Date(at).toISOString();
}

/** Agent 클릭 원장에 쓰는 비식별 멱등 키와 발생 시각을 한 번에 생성합니다. */
export function createAgentClickIdentity(
  pageviewOccurredAt = '',
  createEventId: () => string = () => randomMeasurementId('c'),
  createOccurredAt: () => string = createMeasurementOccurredAt,
): { eventId: string; clickOccurredAt: string } {
  const occurredAt = createOccurredAt();
  return {
    eventId: createEventId(),
    clickOccurredAt: pageviewOccurredAt && occurredAt < pageviewOccurredAt
      ? pageviewOccurredAt
      : occurredAt,
  };
}

export function isCompletePageMeasurementIdentity(
  identity: PageMeasurementIdentity | null,
): identity is PageMeasurementIdentity {
  if (!identity) return false;
  return normalizeMeasurementPagePath(identity.path) === identity.path
    && isValidMeasurementId(identity.pageviewId)
    && isValidMeasurementInstant(identity.pageviewOccurredAt)
    && isValidMeasurementId(identity.sessionId)
    && validMeasurementSource(identity.sessionSource)
    && normalizeMeasurementPagePath(identity.sessionEntryPath) === identity.sessionEntryPath;
}

/** 같은 경로의 페이지뷰와 체류 effect가 실행 순서와 무관하게 공유할 측정 identity입니다. */
export function getOrCreatePageMeasurementIdentity(
  current: PageMeasurementIdentity | null,
  path: string,
  {
    createPageview = createPageviewId,
    createPageviewOccurredAt = createMeasurementOccurredAt,
    getSession = getMeasurementSessionAttribution,
    isDocumentEntry = current === null,
  }: PageMeasurementIdentityOptions = {},
): PageMeasurementIdentity | null {
  const pagePath = normalizeMeasurementPagePath(path);
  if (!pagePath) return null;
  if (current?.path === pagePath && isCompletePageMeasurementIdentity(current)) return current;
  const session = getSession(pagePath, isDocumentEntry);
  const identity = {
    path: pagePath,
    pageviewId: createPageview(),
    pageviewOccurredAt: createPageviewOccurredAt(),
    ...session,
  };
  return isCompletePageMeasurementIdentity(identity) ? identity : null;
}

/** PageShell이 확정한 현재 페이지 identity를 후속 사용자 이벤트와 공유합니다. */
export function setActivePageMeasurementIdentity(identity: PageMeasurementIdentity | null): void {
  activePageMeasurementIdentity = identity;
}

/** 현재 경로와 정확히 일치하는 PageShell identity만 반환하며 새 세션을 만들지 않습니다. */
export function getActivePageMeasurementIdentity(path: string): PageMeasurementIdentity | null {
  const pagePath = normalizeMeasurementPagePath(path);
  return pagePath
    && activePageMeasurementIdentity?.path === pagePath
    && isCompletePageMeasurementIdentity(activePageMeasurementIdentity)
    ? activePageMeasurementIdentity
    : null;
}

/** 새 이벤트 소비자와 기존 source/entryPath 소비자가 함께 읽는 계측 필드 정본입니다. */
export function createPageMeasurementAttribution(
  identity: PageMeasurementIdentity,
  firstSource: string,
  firstEntryPath: string,
): PageMeasurementAttribution {
  return {
    pagePath: identity.path,
    pageviewId: identity.pageviewId,
    pageviewOccurredAt: identity.pageviewOccurredAt,
    sessionId: identity.sessionId,
    source: firstSource,
    entryPath: firstEntryPath,
    firstSource,
    firstEntryPath,
    sessionSource: identity.sessionSource,
    sessionEntryPath: identity.sessionEntryPath,
  };
}

/** 여러 사용자 활동 신호가 같은 60초 제한을 공유하도록 만드는 touch handler입니다. */
export function createMeasurementActivityTouch(
  onTouch: (at: number) => void,
  now: () => number = Date.now,
  initialTouchAt = now(),
): () => void {
  let lastTouchAt = initialTouchAt;
  return () => {
    const at = now();
    if (!Number.isFinite(at) || at - lastTouchAt < MEASUREMENT_SESSION_TOUCH_THROTTLE_MS) return;
    onTouch(at);
    lastTouchAt = at;
  };
}

/** 실제 입력 활동을 관측하고 반환 함수로 모든 listener를 해제합니다. */
export function bindMeasurementActivityListeners(
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>,
  onActivity: EventListener,
): () => void {
  for (const eventName of MEASUREMENT_ACTIVITY_EVENTS) {
    target.addEventListener(eventName, onActivity);
  }
  return () => {
    for (const eventName of MEASUREMENT_ACTIVITY_EVENTS) {
      target.removeEventListener(eventName, onActivity);
    }
  };
}
